import type {
  CalendarItem,
  NormalizedCalendarItem,
  DateTimeFormatOption,
} from './calendar-state';

//#region Series

/**
 * The day name header needs to know the dow for class assignment, and this
 * value should not change based on startingDayOfWeek (i.e., Sunday is always
 * 0). This function computes the dow for a given day index.
 *
 * @param startingDayOfWeek ...
 * @param dayIndex ...
 * @returns ...
 */
export function getColumnDOWClass(startingDayOfWeek: number, dayIndex: number) {
  return `dow${(dayIndex + startingDayOfWeek) % 7}`;
}

export function today() {
  return dateOnly(new Date());
}

export function beginningOfPeriod(
  d: Date,
  periodUom: string,
  startDow: number,
) {
  switch (periodUom) {
    case 'year':
      return new Date(d.getFullYear(), 0);
    case 'month':
      return new Date(d.getFullYear(), d.getMonth());
    case 'week':
      return beginningOfWeek(d, startDow);
    default:
      return d;
  }
}

export function daysOfWeek(weekStart: Date) {
  return [...Array(7)].map((_, i) => addDays(weekStart, i));
}

//#endregion Series

//#region Date Transforms (retain time of day)

export function addDays(d: Date, days: number) {
  return new Date(
    d.getFullYear(),
    d.getMonth(),
    d.getDate() + days,
    d.getHours(),
    d.getMinutes(),
    d.getSeconds(),
  );
}

export function beginningOfWeek(d: Date, startDow: number) {
  return addDays(d, (startDow - d.getDay() - 7) % -7);
}

export function endOfWeek(d: Date, startDow: number) {
  return addDays(beginningOfWeek(d, startDow), 7);
}

//#endregion Date Transforms (retain time of day)

//#region Date Transforms (ignore/wipe time of day)

export function beginningOfMonth(d: Date) {
  return new Date(d.getFullYear(), d.getMonth());
}

export function instanceOfMonth(d: Date) {
  return Math.ceil(d.getDate() / 7);
}

// This export function increments a date by a given number of date units.
// Accepted units are: year, month, week. For year and month, the day of the
// month is unchanged. This could cause an unexpected result if the units are
// "month" and the starting day is higher than the number of days in the
// destination month. The count can be positive or negative.
export function incrementPeriod(d: Date, uom: string, count: number) {
  return new Date(
    d.getFullYear() + (uom == 'year' ? count : 0),
    d.getMonth() + (uom == 'month' ? count : 0),
    d.getDate() + (uom == 'week' ? count * 7 : 0),
  );
}

//#endregion Date Transforms (ignore/wipe time of day)

//#region Date Formatting

export function paddedMonth(d: Date) {
  return ('0' + String(d.getMonth() + 1)).slice(-2);
}

export function paddedDay(d: Date) {
  return ('0' + String(d.getDate())).slice(-2);
}

export function isoYearMonth(d: Date) {
  return d.getFullYear() + '-' + paddedMonth(d);
}

export function isoYearMonthDay(d: Date) {
  return isoYearMonth(d) + '-' + paddedDay(d);
}

export function isoMonthDay(d: Date) {
  return paddedMonth(d) + '-' + paddedDay(d);
}

export function formattedTime(
  d: Date,
  locale: string,
  options?: Intl.DateTimeFormatOptions,
) {
  // Assume midnight = "all day" or indeterminate time
  if (d.getHours() === 0 && d.getMinutes() === 0 && d.getSeconds() === 0)
    return '';

  // If Intl is not supported, send back the 24-hour, zero-padded hours and
  // minutes, expressed as local time.
  if (!supportsIntl()) {
    const ms = new Date().getTimezoneOffset() * 60000; // TZ offset in milliseconds
    return new Date(d.getTime() - ms).toISOString().slice(11, 16);
  }

  return d.toLocaleTimeString(locale, options);
}

/**
 * Formats a date period in long English style. Examples supported:
 *
 * - May 2018
 * - May – June 2018
 * - December 2018 – January 2019
 * - May 6 – 26, 2018
 * - May 13 – June 2, 2018
 * - December 16, 2018 – January 5, 2019
 *
 * @param startDate - The start date of the period.
 * @param endDate - The end date of the period.
 * @param periodUom - The unit of time for the period.
 * @param monthNames - The names of the months.
 * @returns The formatted period.
 */
export function formattedPeriod(
  startDate: Date,
  endDate: Date,
  periodUom: string,
  monthNames: string[],
) {
  const singleYear = startDate.getFullYear() === endDate.getFullYear();
  const singleMonth = isSameMonth(startDate, endDate);
  const isYear = periodUom === 'year';
  const isMonth = periodUom === 'month';
  const isWeek = !isYear && !isMonth;

  const s = [];

  s.push(monthNames[startDate.getMonth()]);

  if (isWeek) {
    s.push(' ');
    s.push(startDate.getDate());
  }

  if (!singleYear) {
    s.push(isWeek ? ', ' : ' ');
    s.push(startDate.getFullYear());
  }

  if (!singleMonth || !singleYear) {
    s.push(' \u2013 ');
    if (!singleMonth) {
      s.push(monthNames[endDate.getMonth()]);
    }
    if (isWeek) s.push(' ');
  } else if (isWeek) {
    s.push(' \u2013 ');
  }

  if (isWeek) {
    s.push(endDate.getDate());
    s.push(', ');
  } else {
    s.push(' ');
  }

  s.push(endDate.getFullYear());

  return s.join('');
}

//#endregion Date Formatting

//#region Date Comparisons

// Number of whole days between two dates. If present, time of day is ignored.
// Treats dates as UTC to avoid DST changes within the period leading to
// incorrect answers (#150).
export function dayDiff(d1: Date, d2: Date) {
  const endDate = Date.UTC(d2.getFullYear(), d2.getMonth(), d2.getDate()),
    startDate = Date.UTC(d1.getFullYear(), d1.getMonth(), d1.getDate());

  return (endDate - startDate) / 86400000;
}

// http://stackoverflow.com/questions/492994/compare-two-dates-with-javascript
export function isSameDate(d1?: Date, d2?: Date) {
  return !!d1 && !!d2 && dayDiff(d1, d2) === 0;
}

export function isSameDateTime(d1?: Date, d2?: Date) {
  return !!d1 && !!d2 && d1.getTime() === d2.getTime();
}

export function isSameMonth(d1?: Date, d2?: Date) {
  return (
    !!d1 &&
    !!d2 &&
    d1.getFullYear() === d2.getFullYear() &&
    d1.getMonth() === d2.getMonth()
  );
}

export function isPastMonth(d: Date) {
  return beginningOfMonth(d) < beginningOfMonth(today());
}

export function isFutureMonth(d: Date) {
  return beginningOfMonth(d) > beginningOfMonth(today());
}

export function isInFuture(d: Date) {
  return dateOnly(d) > today();
}

export function isInPast(d: Date) {
  return dateOnly(d) < today();
}

export function isLastInstanceOfMonth(d: Date) {
  return d.getMonth() !== addDays(d, 7).getMonth();
}

export function isLastDayOfMonth(d: Date) {
  return d.getMonth() !== addDays(d, 1).getMonth();
}

export function fromIsoStringToLocalDate(s: string) {
  const d = [...Array(7)].map((_) => 0);

  s.split(/\D/, 7).forEach((s, i) => (d[i] = Number(s)));
  d[1]--; // adjust month

  return new Date(d[0], d[1], d[2], d[3], d[4], d[5], d[6]);
}

export function toLocalDate(d: any) {
  return typeof d === 'string' ? fromIsoStringToLocalDate(d) : new Date(d);
}

export function dateOnly(d: Date | string) {
  // Always use a copy, setHours mutates argument
  const d2 = new Date(d as unknown as VarDate);

  d2.setHours(0, 0, 0, 0);

  return d2;
}

//#endregion Date Comparisons

//#region Localization

export function languageCode(l: string) {
  return l.substring(0, 2);
}

export function supportsIntl() {
  return typeof Intl !== 'undefined';
}

export type MonthNames = [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

export function getFormattedMonthNames(
  locale: string,
  format: DateTimeFormatOption,
) {
  // Use the provided locale and format if possible to obtain the name of the
  // month
  if (!supportsIntl()) return [...Array(12)].map((_) => '') as MonthNames;

  const formatter = new Intl.DateTimeFormat(locale, { month: format });

  // The year doesn't matter, using 2017 for convenience
  return [...Array(12)].map((_, i) =>
    formatter.format(new Date(2017, i, 1)),
  ) as MonthNames;
}

export type WeekdayNames = [
  string,
  string,
  string,
  string,
  string,
  string,
  string,
];

export function getFormattedWeekdayNames(
  locale: string,
  format: DateTimeFormatOption,
  startingDayOfWeek: number,
) {
  // Use the provided locale and format if possible to obtain the name of the
  // days of the week
  if (!supportsIntl()) return [...Array(7)].map((_) => '') as WeekdayNames;

  const formatter = new Intl.DateTimeFormat(locale, { weekday: format });

  // 2017 starts on Sunday, so use it as the baseline date
  return [...Array(7)].map((_, i) =>
    formatter.format(new Date(2017, 0, (i + 1 + startingDayOfWeek) % 7)),
  ) as WeekdayNames;
}

export function getDefaultBrowserLocale() {
  return navigator.languages[0]?.toLowerCase() as string;
}

//#endregion Localization

export function normalizeItem(item: CalendarItem, isHovered: boolean) {
  // Starting in version 6, classes must be an array of string Classes may be a
  // string, an array, or null. Normalize to an array
  const itemClasses = item.classes ? [...item.classes] : [];

  // Provides support for pseudo-hover of entire item when one part of it is
  // hovered
  if (isHovered) itemClasses.push('isHovered');

  return {
    originalItem: item,
    startDate: toLocalDate(item.startDate),
    // For an item without an end date, the end date is the start date
    endDate: toLocalDate(item.endDate || item.startDate),
    classes: itemClasses,
    // Items without a title are untitled
    title: item.title || 'Untitled',
    // An ID is *required*. Auto-generating leads to weird bugs because these
    // are used as keys and passed in items
    id: item.id,
    // Pass the URL along
    url: item.url,
    // Use the item's title as the tooltip if the tooltip is undefined or null
    // (but not if it is blank -- use blank to essentially disable tooltips)
    tooltip: item.tooltip ?? item.title,
  } as NormalizedCalendarItem;
}
